import React, { useEffect, useRef, useState } from 'react';
import { CardContent, IconButton, TextField, Tooltip } from '@mui/material';
import { PlayArrowRounded, StopRounded, ClearRounded, SaveRounded } from '@mui/icons-material';
import { LatencyMeasurePoint } from 'core/models/latency';

function calcAverageLatency(points: LatencyMeasurePoint) {
    let sum = 0;
    let cnt = 0;
    points.forEach(point => {
        point.cameras.forEach(camera => {
            camera.channels.forEach(ch => {
                sum += ch.measurement.totalLatency;
                if (ch.measurement.totalLatency !== 0) cnt++;
            });
        });
    });

    return cnt > 0 ? sum / cnt : undefined;
}

const invalidMarker = '--';

export type AutomaticLatencyMeasurePanelProps = {
    points: LatencyMeasurePoint;
    onStart: (intervalMS: number) => void;
    onStop: () => void;
    onReset: () => void;
    onSave: () => void;
};

export function AutomaticLatencyMeasurePanel(props: AutomaticLatencyMeasurePanelProps) {
    const { points, onStart: start, onStop: stop, onReset: reset, onSave: save } = props;

    const [measuringHz, setMeasuringHz] = useState(2);

    const [startAt, setStartAt] = useState<Date>();
    const [stopAt, setStopAt] = useState<Date>();

    const started = (startAt === undefined) !== (stopAt === undefined);

    const durationClockHz = 10;
    const durationRef = useRef<HTMLElement>(null);
    const lastTime = useRef(0);
    useEffect(() => {
        const elem = durationRef.current;
        if (!elem) return;

        const interval = setInterval(() => {
            if (started) {
                lastTime.current = Date.now();
            }
            elem.textContent = startAt
                ? `${((lastTime.current - startAt.getTime()) / 1000).toFixed(1)}ms`
                : invalidMarker;
        }, 1000 / durationClockHz);
        return () => clearInterval(interval);
    }, [startAt, started]);

    const average = calcAverageLatency(points);

    return (
        <CardContent>
            <Tooltip title={started ? 'Stop' : 'Start'} placement='top'>
                <IconButton
                    onClick={() => {
                        if (started) {
                            stop();
                            setStopAt(new Date(Date.now()));
                        } else {
                            reset();
                            setStopAt(undefined);
                            setStartAt(new Date(Date.now()));
                            start(1000 / measuringHz);
                        }
                    }}
                    size="large"
                >
                    {started ? <StopRounded /> : <PlayArrowRounded />}
                </IconButton>
            </Tooltip>
            <Tooltip title='Reset' placement='top'>
                <IconButton
                    onClick={() => {
                        reset();
                        setStopAt(undefined);
                        setStartAt(undefined);
                    }}
                    size="large"
                    disabled={startAt === undefined || stopAt === undefined}
                >
                    <ClearRounded />
                </IconButton>
            </Tooltip>
            <Tooltip title='Save' placement='top'>
                <IconButton
                    onClick={() => save()}
                    size="large"
                    disabled={stopAt === undefined}
                >
                    <SaveRounded />
                </IconButton>
            </Tooltip>
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                Measuring Frequency: <TextField
                    type='number'
                    value={measuringHz}
                    onChange={e => setMeasuringHz(Number.parseFloat(e.target.value))}
                    disabled={started}
                    inputProps={{ sx: { padding: '0 0.5em', width: '4em' } }}
                /> Hz
            </div>
            <div>Start At: {startAt?.toLocaleTimeString() ?? invalidMarker}</div>
            <div>Stop At: {stopAt?.toLocaleTimeString() ?? invalidMarker}</div>
            <div>Duration: <span ref={durationRef}>{invalidMarker}</span></div>
            <div>Measured Points: {points.length}</div>
            <div>Average Latency: {average !== undefined ? `${average.toFixed(3)} msec` : invalidMarker}</div>
        </CardContent>
    );
}
